/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { ENVIRONMENT, isNonProduction, isScreenshotMode, type Environment } from '@shared/utils/environment';
import { useStateSimulatorStore } from '@/features/dev-cockpit/services/state-simulator';
import { Text, UnstyledButton } from '@/shared/ui';

const BOOKMARK_WIDTH = 22;
const BOOKMARK_HEIGHT = 56;
const NOTCH_DEPTH = 8;

const palette: Record<Exclude<Environment, 'production'>, { from: string; to: string; glow: string; label: string }> = {
  local:   { from: 'var(--status-success)', to: '#166534', glow: 'color-mix(in srgb, var(--status-success) 40%, transparent)', label: 'LOCAL' },
  testing: { from: 'var(--status-info)', to: '#1e40af', glow: 'color-mix(in srgb, var(--status-info) 40%, transparent)', label: 'TEST' },
  staging: { from: 'var(--status-error)', to: '#7f1d1d', glow: 'color-mix(in srgb, var(--status-error) 40%, transparent)', label: 'STG' },
};

interface EnvironmentBookmarkProps {
  onClick?: () => void;
}

export const EnvironmentBookmark: React.FC<EnvironmentBookmarkProps> = ({ onClick }) => {
  // Hooks must run unconditionally and before any early return (React rules),
  // so subscribe to the runtime screenshot-mode flag at the very top.
  const runtimeScreenshotMode = useStateSimulatorStore((s) => s.screenshotMode);

  if (!isNonProduction) return null;
  // Screenshot mode hides the ribbon so clean local screenshots can be taken.
  // It is active via the env var OR the Dev Cockpit runtime toggle.
  if (isScreenshotMode || runtimeScreenshotMode) return null;

  const env = ENVIRONMENT as Exclude<Environment, 'production'>;
  const colors = palette[env] ?? palette.local;
  const isClickable = !!onClick;
  const w = BOOKMARK_WIDTH;
  const h = BOOKMARK_HEIGHT;
  const nd = NOTCH_DEPTH;
  const ribbonBottom = h - nd;

  return (
    <div
      className="fixed top-0 z-10001 select-none group"
      style={{ right: 'var(--space-1)', width: w, height: h }}
      aria-label={`Environment: ${env}`}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        className="absolute inset-0 block"
        style={{ filter: `drop-shadow(0 4px 8px ${colors.glow})` }}
      >
        <defs>
          <linearGradient id="env-ribbon" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.from} />
            <stop offset="100%" stopColor={colors.to} />
          </linearGradient>
        </defs>
        <path
          d={`M2 0 H${w - 2} Q${w} 0 ${w} 2 V${ribbonBottom} L${w / 2} ${h} L0 ${ribbonBottom} V2 Q0 0 2 0 Z`}
          fill="url(#env-ribbon)"
        />
        <line
          x1="1.5" y1="2" x2="1.5" y2={ribbonBottom - 4}
          stroke="white" strokeOpacity="0.2" strokeWidth="1"
        />
        <line
          x1={w - 1.5} y1="2" x2={w - 1.5} y2={ribbonBottom - 4}
          stroke="black" strokeOpacity="0.15" strokeWidth="1"
        />
      </svg>

      <Text component="span"
        className="absolute pointer-events-none"
        style={{
          top: 'var(--space-2)',
          left: 0,
          width: w,
          height: ribbonBottom - 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'rotate(180deg) scale(0.75)',
          color: 'var(--text-inverse)',
          fontSize: 'var(--text-meta)',
          fontWeight: 800,
          letterSpacing: 'var(--tracking-widest)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textTransform: 'uppercase',
          lineHeight: 1,
          userSelect: 'none',
          textShadow: '0 1px 2px color-mix(in srgb, var(--text-primary) 30%, transparent)',
        }}
      >
        {colors.label}
      </Text>

      {isClickable && (
        <UnstyledButton
          onClick={onClick}
          className="absolute inset-0 border-none p-0 m-0 cursor-pointer bg-transparent transition-all duration-150"
          style={{ width: w, height: h }}
          aria-label="Toggle Dev Cockpit (F2)"
        >
          <svg
            viewBox={`0 0 ${w} ${h}`}
            width={w}
            height={h}
            className="block opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          >
            <path
              d={`M2 0 H${w - 2} Q${w} 0 ${w} 2 V${ribbonBottom} L${w / 2} ${h} L0 ${ribbonBottom} V2 Q0 0 2 0 Z`}
              fill="white"
              opacity="0.18"
            />
          </svg>
        </UnstyledButton>
      )}
    </div>
  );
};

export default EnvironmentBookmark;
